import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types';
import { addressFromPlace, type AddressValue } from './address-value';

export type AddressSuggestion = components['schemas']['AddressSuggestion'];

/** Board 2.1: suggestions after 3 characters and a 300 ms pause. */
export const MIN_INPUT = 3;
export const DEBOUNCE_MS = 300;
/** The service's limit on what is typed. */
export const MAX_INPUT = 200;
/**
 * After «unavailable» the field stops asking for this long: the service answers the same way until
 * the key exists (#964), and a person typing by hand should not send a request per keystroke.
 */
export const UNAVAILABLE_PAUSE_MS = 30_000;

export type AddressSearchStatus =
  /** Nothing to suggest: under 3 characters, or the list was closed. */
  | 'idle'
  /** «Cerco…»: waiting for the pause or for the answer. */
  | 'searching'
  /** The suggestions arrived (possibly none). */
  | 'ready'
  /** The service cannot suggest nor fill now: type by hand (board 2.6). */
  | 'unavailable';

export type PlaceOutcome =
  | { readonly status: 'ok'; readonly address: AddressValue }
  | { readonly status: 'not-found' }
  | { readonly status: 'unavailable' };

/** One search's session token (`[A-Za-z0-9_-]{16,36}`): a UUID, as the address service asks. */
function newSessionToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * The address service's two calls for one search (docs/specifications/address-service.md): the
 * suggestions while typing and the place that ends the search, under the same session token so
 * Google bills them as one session. A new token starts with the next search. Any failure, thrown
 * or answered, is «unavailable»: the form goes on by hand and is never blocked.
 */
export function useAddressSearch(language: 'it' | 'en') {
  const [status, setStatus] = useState<AddressSearchStatus>('idle');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const token = useRef<string | null>(null);
  const request = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pausedUntil = useRef(0);

  const cancelPending = useCallback(() => {
    request.current += 1;
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => cancelPending, [cancelPending]);

  const markUnavailable = useCallback(() => {
    pausedUntil.current = Date.now() + UNAVAILABLE_PAUSE_MS;
    setSuggestions([]);
    setStatus('unavailable');
  }, []);

  const search = useCallback(
    (text: string) => {
      cancelPending();
      const input = text.trim().slice(0, MAX_INPUT);
      if (Date.now() < pausedUntil.current) {
        setStatus('unavailable');
        return;
      }
      if (input.length < MIN_INPUT) {
        setSuggestions([]);
        setStatus('idle');
        return;
      }
      setStatus('searching');
      const id = request.current;
      timer.current = setTimeout(() => {
        timer.current = null;
        token.current ??= newSessionToken();
        const sessionToken = token.current;
        void (async () => {
          try {
            const { data } = await apiClient.POST('/v1/addresses/suggestions', {
              body: { input, sessionToken, language },
            });
            if (id !== request.current) {
              return;
            }
            if (data?.status !== 'ok') {
              markUnavailable();
              return;
            }
            setSuggestions(data.suggestions ?? []);
            setStatus('ready');
          } catch {
            if (id === request.current) {
              markUnavailable();
            }
          }
        })();
      }, DEBOUNCE_MS);
    },
    [cancelPending, language, markUnavailable],
  );

  /** Closes the list and leaves the text: the search's token is kept for the next keystroke. */
  const close = useCallback(() => {
    cancelPending();
    setSuggestions([]);
    setStatus((current) => (current === 'unavailable' ? current : 'idle'));
  }, [cancelPending]);

  /** The structured address of a suggestion; it ends the search, the next one gets a new token. */
  const choose = useCallback(
    async (suggestion: AddressSuggestion): Promise<PlaceOutcome> => {
      cancelPending();
      setSuggestions([]);
      setStatus('idle');
      const sessionToken = token.current ?? newSessionToken();
      token.current = null;
      try {
        const { data } = await apiClient.GET('/v1/addresses/places/{placeId}', {
          params: { path: { placeId: suggestion.placeId }, query: { sessionToken, language } },
        });
        if (data?.status === 'ok' && data.address !== undefined) {
          return { status: 'ok', address: addressFromPlace(data.address) };
        }
        if (data?.status === 'not-found') {
          return { status: 'not-found' };
        }
      } catch {
        // Unreachable: handled as «unavailable» below.
      }
      markUnavailable();
      return { status: 'unavailable' };
    },
    [cancelPending, language, markUnavailable],
  );

  return { status, suggestions, search, close, choose };
}
